package com.islamicmedia.site.components

import androidx.compose.runtime.*
import com.islamicmedia.shared.domain.model.DisambiguationItem
import com.islamicmedia.site.theme.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private fun mediaTypeEmoji(type: String?): String = when (type?.lowercase()) {
    "movie", "movies" -> "\uD83C\uDFAC"
    "tv_show", "tv_shows", "tv show", "tv series" -> "\uD83D\uDCFA"
    "game", "games", "video game", "video_game" -> "\uD83C\uDFAE"
    "youtube", "youtube channel" -> "\uD83D\uDCF9"
    "book", "books" -> "\uD83D\uDCDA"
    else -> "\uD83C\uDFAC"
}

private fun mediaTypeLabel(type: String?): String = when (type?.lowercase()) {
    "movie", "movies" -> "Movie"
    "tv_show", "tv_shows", "tv show", "tv series" -> "TV Show"
    "game", "games", "video game", "video_game" -> "Game"
    "youtube", "youtube channel" -> "YouTube"
    "book", "books" -> "Book"
    else -> "Media"
}

@Composable
fun DisambiguationList(
    items: List<DisambiguationItem>,
    onItemSelected: (DisambiguationItem) -> Unit
) {
    var showAll by remember { mutableStateOf(false) }
    val displayItems = if (showAll) items else items.take(5)

    Div(attrs = {
        classes("card-hover")
        style {
            property("background", GradientCard)
            borderRadius(RadiusXLarge.unsafeCast<CSSNumeric>())
            padding(32.px)
            property("box-shadow", CardShadow)
            property("border", CardBorder)
        }
    }) {
        Div(attrs = {
            style {
                fontSize(22.px)
                fontWeight(700)
                marginBottom(6.px)
                property("background", GradientPrimary)
                property("-webkit-background-clip", "text")
                property("-webkit-text-fill-color", "transparent")
                property("background-clip", "text")
            }
        }) { Text("Which one did you mean?") }

        P(attrs = {
            style {
                margin(0.px, 0.px, 20.px)
                color(Gray500)
                fontSize(15.px)
            }
        }) { Text("Tap the one you want to check:") }

        displayItems.forEachIndexed { index, item ->
            Div(attrs = {
                onClick { onItemSelected(item) }
                style {
                    backgroundColor(Color.white)
                    borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
                    padding(18.px, 22.px)
                    marginBottom(12.px)
                    cursor("pointer")
                    border(1.px, LineStyle.Solid, Gray200)
                    property("transition", TransitionMedium)
                    property("box-shadow", "0 1px 3px rgba(0,0,0,0.04)")
                }
                onMouseOver {
                    (it.currentTarget as? org.w3c.dom.HTMLElement)?.let { el ->
                        el.style.borderColor = "#14A3A0"
                        el.style.transform = "translateX(6px)"
                        el.style.boxShadow = "0 4px 12px rgba(14, 124, 123, 0.15)"
                    }
                }
                onMouseOut {
                    (it.currentTarget as? org.w3c.dom.HTMLElement)?.let { el ->
                        el.style.borderColor = "#E5E5E5"
                        el.style.transform = "translateX(0)"
                        el.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"
                    }
                }
            }) {
                // Title row with badge
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.FlexStart)
                        gap(12.px)
                    }
                }) {
                    Div(attrs = {
                        style {
                            fontSize(17.px)
                            fontWeight(600)
                            color(Gray900)
                            property("flex", "1")
                        }
                    }) { Text(item.title) }

                    // Media type badge
                    Div(attrs = {
                        style {
                            property("background", "linear-gradient(135deg, rgba(14, 124, 123, 0.08) 0%, rgba(20, 163, 160, 0.08) 100%)")
                            borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
                            padding(8.px, 14.px)
                            fontSize(13.px)
                            fontWeight(600)
                            color(Primary)
                            property("white-space", "nowrap")
                            property("flex-shrink", "0")
                        }
                    }) {
                        Text(mediaTypeLabel(item.mediaType))
                    }
                }

                // Description (full width)
                val subtitle = listOfNotNull(item.author?.let { "by $it" }, item.year, item.info).filter { it.isNotBlank() }.joinToString(" \u2022 ")
                if (subtitle.isNotEmpty()) {
                    Div(attrs = {
                        style {
                            fontSize(14.px)
                            color(Gray500)
                            marginTop(4.px)
                        }
                    }) { Text(subtitle) }
                }
            }
        }

        if (!showAll && items.size > 5) {
            Button(attrs = {
                onClick { showAll = true }
                style {
                    width(100.percent)
                    marginTop(8.px)
                    padding(16.px)
                    borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
                    border(2.px, LineStyle.Solid, PrimaryLight)
                    backgroundColor(Color.transparent)
                    color(PrimaryLight)
                    fontSize(15.px)
                    fontWeight(600)
                    cursor("pointer")
                    property("font-family", "inherit")
                    property("transition", TransitionMedium)
                }
            }) {
                Text("Show ${items.size - 5} more...")
            }
        }
    }
}
